import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    Body1,
    Body2,
    Box,
    Flex,
    IconChevronRight,
    Subtitle2,
    TextButton,
} from '@myphx-engineering/phx-design-system';
import { MedFormIcon } from '@phx/myphx-lib';

import { useMedicationConfigOverride } from '../../../../contexts/DrugProgramProvider';
import { getFragment } from '../../../../graphql/client/helpers/get-fragment';
import {
    type DigitallyReceivedPrescriberOrderBasicInfoFragment,
    PrescriberOrderStatus,
    RenewalRequestStatus,
} from '../../../../graphql/generated/graphql';
import { useFlags } from '../../../../hooks/use-flags';
import { formatToLocalizedDate } from '../../../../util';
import { getProductDescription } from '../../../../util/formatting/prescriber-order/get-product-description';
import { getAbsoluteRoute } from '../../../../util/get-absolute-route';

export const HistorySearchItem = ({
    item,
}: {
    item: DigitallyReceivedPrescriberOrderBasicInfoFragment;
}) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const configOverride = useMedicationConfigOverride();
    const { enableRxCancelLilly, enableRxRenewalLilly } = useFlags();

    const { assignmentTimestamp, authorizedMedication, cancelDetails } = item;

    const patient = getFragment(item.patient);
    const med = getFragment(authorizedMedication);

    const ndcKey =
        med.__typename === 'UnknownMedication' && med.originalNdc
            ? med.originalNdc
            : med.id;
    const medicationOverride = configOverride.ndcInfo[ndcKey];
    const form =
        med.__typename === 'AuthorizedMedication'
            ? med.formCode
            : (medicationOverride?.dosageForm ?? null);
    const cancelledPrescriberOrder =
        enableRxCancelLilly && item.status === PrescriberOrderStatus.Cancelled;

    const pendingPrescriberOrder =
        enableRxRenewalLilly &&
        item.renewalRequestDetails?.state === RenewalRequestStatus.Sent;

    const productDescription = getProductDescription(
        item,
        i18n,
        medicationOverride
    );

    const latestActionTimestamp =
        cancelDetails?.timestamp ?? assignmentTimestamp;
    const formattedlatestActionDate = latestActionTimestamp
        ? formatToLocalizedDate(
              new Date(latestActionTimestamp),
              { dateStyle: 'medium' },
              i18n.language
          )
        : null;

    const navigateToOrderSummary = () => {
        navigate(
            getAbsoluteRoute('order.prescriberOrderId.summary', {
                prescriberOrderId: item.id,
            }),
            { state: { prev: 'history' } }
        );
    };

    return (
        <Flex
            gap="xs"
            justify="space-between"
            align="start"
            onClick={enableRxCancelLilly ? undefined : navigateToOrderSummary}
            style={
                enableRxCancelLilly
                    ? undefined
                    : { cursor: 'pointer', userSelect: 'none' }
            }
        >
            <Flex gap="xs">
                <MedFormIcon form={form} />
                <Box>
                    <Subtitle2>{productDescription}</Subtitle2>
                    {cancelledPrescriberOrder ? (
                        <Body1>{t('cabinet.history.cancelled')}</Body1>
                    ) : null}
                    <Body1>
                        {t('cabinet.medicationOrder.forName', {
                            name: patient?.fullName,
                        })}
                    </Body1>
                    {pendingPrescriberOrder && (
                        <Body1>{t('cabinet.history.pendingApproval')}</Body1>
                    )}
                    <TextButton
                        RightIcon={IconChevronRight}
                        onClick={navigateToOrderSummary}
                    >
                        {t('orderSummary.viewDetails')}
                    </TextButton>
                </Box>
            </Flex>
            {formattedlatestActionDate ? (
                <Body2 noWrap>{formattedlatestActionDate}</Body2>
            ) : null}
        </Flex>
    );
};
